import { reaction } from "mobx";
import { useEffect, useRef } from "react";

import type { DataModel, SelectionMode } from "../../../../models/data-model";

/**
 * Bundles all the effects that mirror DataTable props into the underlying MobX model
 */
export function useModelSync<T extends object, K extends keyof T>({
  model,
  canSelect,
  preselected,
  onSelect,
  itemKeyField,
}: {
  model: DataModel<T>;
  canSelect: SelectionMode | undefined;
  preselected: T[K][] | undefined;
  onSelect: ((selected: T[K][]) => void) | undefined;
  itemKeyField: K;
}) {
  // Set during render (not in an effect) so it's correct before any row cell
  // reads it. The edit-action cell keys off `item[idAttr]`; a stale `undefined`
  // makes its guard `editingItemId !== item[idAttr]` read `undefined !== undefined`,
  // so every row renders the inline Save/Cancel buttons instead of Edit. `idAttr`
  // isn't observable, so a post-paint effect write wouldn't reliably propagate to
  // cells that already rendered.
  model.idAttr = itemKeyField;

  useEffect(() => {
    if (canSelect) {
      model.selectionMode = canSelect;
    }
  }, [model, canSelect]);

  useEffect(() => {
    // Compare by value, not array identity: a caller may mirror `model.selected`
    // straight back into `preselected` (a controlled two-way binding), handing
    // us a fresh array with identical ids every render. Re-copying that would
    // re-fire the onSelect reaction below and loop forever.
    if (preselected && !sameIds(model.selected, preselected)) {
      // Copy — the model mutates `selected` in place on later selections.
      model.selected = [...preselected];
    }
  }, [model, preselected]);

  // Hold the latest `onSelect` in a ref so the reaction below can subscribe once
  // instead of resubscribing whenever the (typically inline) closure identity
  // changes — resubscribing re-runs `fireImmediately` and echoes the selection
  // back on every render, the other half of the two-way-binding loop.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // A MobX reaction (not a `model.selected` effect dep) because the model
  // mutates the array in place on additive multi-select — the identity only
  // changes on deselect/select-all, so an effect would miss those updates.
  useEffect(() => {
    return reaction(
      () => model.selected.slice(),
      (selected) => onSelectRef.current?.(selected),
      { fireImmediately: true },
    );
  }, [model]);
}

/** Order-insensitive equality for selection id lists (no duplicates expected). */
function sameIds<V>(a: V[], b: V[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}
